import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Phone, MapPin, Send, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ScrollReveal, { StaggerGroup } from '@/components/ScrollReveal';
import StarBorderButton from '@/components/StarBorderButton';
import SpotlightCard from '@/components/SpotlightCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service_type: '',
    message: '',
    budget: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Pesan berhasil dikirim!', {
        description: 'Kami akan segera menghubungi Anda. Terima kasih!'
      });
      setFormData({
        name: '',
        email: '',
        service_type: '',
        message: '',
        budget: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Gagal mengirim pesan', {
        description: 'Silakan coba lagi atau hubungi via WhatsApp.'
      });
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      label: 'Email',
      value: 'arnataproject@gmail.com',
      link: 'mailto:arnataproject@gmail.com'
    },
    {
      icon: <Phone size={24} />,
      label: 'WhatsApp',
      value: '+62 851-8334-7683',
      link: 'https://wa.me/6285183347683'
    },
    {
      icon: <MapPin size={24} />,
      label: 'Location',
      value: 'Way Halim, Bandar Lampung, Lampung, Indonesia',
      link: null
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-20 px-6 noise-overlay">
        <div className="container mx-auto text-center">
          <MessageSquare size={64} className="text-cyan-400 mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="contact-title">
            Mari Bicara Tentang
            <br />
            <span className="gradient-text">Project Anda</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Ceritakan kebutuhan Anda, kami bantu breakdown teknis dan solusinya. Konsultasi awal gratis!
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="pb-20 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Contact Info */}
            <ScrollReveal animation="fade-right" className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Hubungi Kami
                </h2>
                <p className="text-slate-400 text-sm">
                  Pilih cara yang paling nyaman untuk Anda. Kami responsif dan siap membantu!
                </p>
              </div>

              <StaggerGroup staggerDelay={0.1} animation="fade-right" className="space-y-6">
                {contactInfo.map((info, index) => (
                  <SpotlightCard key={index} className="glass-effect border-slate-700 p-6 group hover:border-cyan-500/50 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/20 transition-all duration-300" data-testid={`contact-info-${index}`}>
                    <div className="flex items-start space-x-4">
                      <div className="text-cyan-400 mt-1 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">{info.icon}</div>
                      <div>
                        <div className="text-slate-500 text-xs mb-1">{info.label}</div>
                        {info.link ? (
                          <a 
                            href={info.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-white hover:text-cyan-400 transition-colors font-medium"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <div className="text-white font-medium">{info.value}</div>
                        )}
                      </div>
                    </div>
                  </SpotlightCard>
                ))}

                <Card className="glass-effect border-cyan-500/30 bg-cyan-500/5 p-6 group hover:border-cyan-500/50 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-300">
                  <h3 className="text-white font-semibold mb-2 transition-colors duration-300 group-hover:text-cyan-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Quick Response
                  </h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Butuh respons cepat? Chat langsung via WhatsApp untuk diskusi instant!
                  </p>
                  <a href="https://wa.me/6281234567890" target="_blank" rel="noopener noreferrer">
                    <StarBorderButton className="w-full btn-primary group/btn" data-testid="whatsapp-quick-btn">
                      <Phone size={18} className="transition-transform duration-300 group-hover/btn:rotate-12" />
                      Chat WhatsApp
                    </StarBorderButton>
                  </a>
                </Card>
              </StaggerGroup>
            </ScrollReveal>

            {/* Contact Form */}
            <ScrollReveal animation="fade-left" className="md:col-span-2">
              <Card className="glass-effect border-slate-700 p-8 hover:border-cyan-500/30 transition-all duration-300" data-testid="contact-form">
                <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Kirim Pesan
                </h2>
                
                <form action="https://send.pageclip.co/pYmWq5aiaV4LqatLbz4o9O1hBZWwXGUd" className="pageclip-form space-y-6" method="post">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name" className="text-slate-300 mb-2 block">Nama Lengkap *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Andika"
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                      data-testid="contact-name-input"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-slate-300 mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="dika@example.com"
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                      data-testid="contact-email-input"
                    />
                  </div>

                  {/* Service Type */}
                  <div>
                    <Label htmlFor="service_type" className="text-slate-300 mb-2 block">Jenis Layanan *</Label>
                    <input type="hidden" name="service_type" value={formData.service_type} />
                    <Select onValueChange={handleServiceChange} value={formData.service_type} required>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="contact-service-select">
                        <SelectValue placeholder="Pilih jenis layanan" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 text-white">
                        <SelectItem value="web-development">Web Development</SelectItem>
                        <SelectItem value="iot-development">IoT Development</SelectItem>
                        <SelectItem value="devops">DevOps & Cloud</SelectItem>
                        <SelectItem value="troubleshooting">Troubleshooting & Maintenance</SelectItem>
                        <SelectItem value="engineering-support">Engineering Support</SelectItem>
                        <SelectItem value="mentoring">Mentoring</SelectItem>
                        <SelectItem value="desain-pcb">Desain PCB</SelectItem>
                        <SelectItem value="desain-skematik">Desain Skematik</SelectItem>
                        <SelectItem value="jasa-solder">Jasa Solder</SelectItem>
                        <SelectItem value="Install-ulang">Install ulang windows</SelectItem>
                        <SelectItem value="Bersihin-Laptop">Cleaning Laptop, Ganti SSD, Ganti RAM</SelectItem>
                        <SelectItem value="other">Lainnya</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-slate-300 mb-2 block">Deskripsi Kebutuhan *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Ceritakan project atau kebutuhan Anda secara singkat..."
                      rows={5}
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 resize-none"
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  {/* Budget (Optional) */}
                  <div>
                    <Label htmlFor="budget" className="text-slate-300 mb-2 block">Budget (Opsional)</Label>
                    <Input
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      placeholder="Misal: Rp 50.000 - 1.000.000"
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                      data-testid="contact-budget-input"
                    />
                    <p className="text-slate-500 text-xs mt-1">Membantu kami memberikan rekomendasi yang sesuai</p>
                  </div>

                  {/* Submit Button */}
                  <StarBorderButton 
                    type="submit" 
                    className="w-full btn-primary text-base py-6 pageclip-form__submit"
                    data-testid="contact-submit-btn"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Send size={20} />
                      Kirim Pesan
                    </span>
                  </StarBorderButton>
                </form>

                <div className="mt-6 pt-6 border-t border-slate-700">
                  <p className="text-slate-400 text-xs text-center">
                    Dengan mengirim form ini, Anda setuju bahwa kami akan menghubungi Anda terkait kebutuhan project Anda.
                  </p>
                </div>
              </Card>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Response Time */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <Card className="glass-effect border-slate-700 p-8">
              <h3 className="text-xl font-bold text-white mb-6 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Apa yang Terjadi Setelah Anda Menghubungi?
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    step: '1',
                    title: 'Respons Cepat',
                    desc: 'Kami akan membalas dalam 1-24 jam (biasanya lebih cepat!)'
                  },
                  {
                    step: '2',
                    title: 'Diskusi Detail',
                    desc: 'Breakdown kebutuhan, teknologi, timeline, dan estimasi biaya'
                  },
                  {
                    step: '3',
                    title: 'Proposal',
                    desc: 'Anda terima proposal lengkap untuk review dan approval'
                  }
                ].map((item, index) => (
                  <div key={index} className="text-center" data-testid={`response-step-${index}`}>
                    <div className="w-12 h-12 bg-cyan-500/20 text-cyan-400 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {item.step}
                    </div>
                    <h4 className="text-white font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {item.title}
                    </h4>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}